import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const http = axios.create({ baseURL: API, withCredentials: true });

// Refresh the short-lived access cookie once on a 401, then retry.
http.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error.config;
    if (error.response?.status === 401 && !cfg._retry && !cfg.url.startsWith("/auth/")) {
      cfg._retry = true;
      try {
        await http.post("/auth/refresh");
        return http(cfg);
      } catch {
        /* fall through */
      }
    }
    return Promise.reject(error);
  }
);

export const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = signed out
  const check = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/me");
      setUser(data);
    } catch {
      try {
        const { data } = await http.post("/auth/refresh");
        setUser(data);
      } catch {
        setUser(false);
      }
    }
  }, []);
  useEffect(() => {
    if (window.location.pathname.startsWith("/admin")) check();
    else setUser(false);
  }, [check]);
  const login = async (email, password) => {
    const { data } = await http.post("/auth/login", { email, password });
    setUser(data);
  };
  const logout = async () => {
    await http.post("/auth/logout");
    setUser(false);
  };
  return <AuthContext.Provider value={{ user, login, logout, check }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
